import React, { useEffect } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import classNames from "classnames/bind";
import Button from "common/blocks/Button";
import RichText from "common/blocks/RichText";
import TextInput from "common/blocks/TextInput";
import useModalContext from "common/providers/modal/useModalContext";
import usePartExchangeQuestionsContext from "features/vehicleDetails/providers/partExchangeQuestions/usePartExchangeQuestionsContext";
import useKeysValidation from "hooks/useKeysValidation/useKeysValidation";
import { DIGITS_REGEX } from "lib/constants";
import isNumber from "lodash/isNumber";
import { useForm } from "react-hook-form";

import { vehicleDetailsModal } from "..";
import TooUnique from "../common/TooUnique";
import Wrapper from "../common/Wrapper";
import styles from "./ValuePartExchangeForm.module.scss";
import { partExchangeSchema } from "./schema";

const cx = classNames.bind(styles);

export type ValuePartExchangeFormState = {
    mileage: string;
    registration_number: string;
    reason_for_valuation?: string;
};

type ValuePartExchangeFormProps = {
    params: {
        registration_number?: string;
    };
};

const ValuePartExchangeForm = ({ params }: ValuePartExchangeFormProps) => {
    const { useSidebar } = useModalContext();
    const { openSidebar, closeSidebar, registerOnCloseCallback, setSidebarTitle } = useSidebar();
    const {
        getPartExQuestions,
        resetAllQuestionsData,
        setGetModalContentData,
        updateFormAnswers,
        modalContent,
        partExQuestions,
        tooUnique,
        partExQuestionsVehicle,
    } = usePartExchangeQuestionsContext();
    const { handleKeyDownEvent, handlePasteEvent } = useKeysValidation();

    useEffect(() => {
        setGetModalContentData(true);
    });

    useEffect(() => {
        if (modalContent?.modalTitle) setSidebarTitle(modalContent.modalTitle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalContent?.modalTitle]);

    const { CapValuation: { VRM, Mileage } = { VRM: undefined, Mileage: undefined } } =
        partExQuestions ?? {};
    const { watch, register, setValue, handleSubmit, formState, reset } =
        useForm<ValuePartExchangeFormState>({
            defaultValues: {
                registration_number: params?.registration_number ?? VRM?.toUpperCase() ?? "",
                mileage: Mileage?.toString() ?? undefined,
            },
            resolver: zodResolver(partExchangeSchema),
        });

    const onSubmit = async (formData: ValuePartExchangeFormState) => {
        const { registration_number, mileage } = formData;
        const questions = await getPartExQuestions({
            registration_number,
            mileage,
        });

        const reason_for_valuation = questions?.PartExchangeQuestionViewModels.find(
            (x) => x.Identifier === "reason_for_valuation"
        )
            ? "part_exchange"
            : "";

        if (questions && reason_for_valuation) {
            updateFormAnswers("UserAnswers", {
                registration_number,
                mileage: mileage.toString(),
                reason_for_valuation: reason_for_valuation,
            });
            openSidebar(vehicleDetailsModal);
        } else if (questions) {
            updateFormAnswers("UserAnswers", {
                registration_number,
                mileage: mileage.toString(),
            });
            openSidebar(vehicleDetailsModal);
        }
    };

    const onValueNewVehicle = () => {
        resetAllQuestionsData();
        reset();
    };

    if (!partExQuestionsVehicle) registerOnCloseCallback(onValueNewVehicle);

    if (!!tooUnique) {
        return (
            <Wrapper>
                <TooUnique
                    tooUnique={tooUnique}
                    onValueNewVehicle={onValueNewVehicle}
                    closeModal={closeSidebar}
                />
            </Wrapper>
        );
    }

    const MAX_MILEAGE = 500000;

    return !!modalContent ? (
        <Wrapper>
            <div className={cx("partExchangeWrapper")}>
                <div className={cx("info")}>
                    {modalContent?.modalText && <RichText {...modalContent?.modalText} />}
                </div>
                <span className={cx("title")}>Vehicle Search</span>
                <form className={cx("partExchange")} onSubmit={handleSubmit(onSubmit)}>
                    <TextInput
                        maxLength={7}
                        {...register("registration_number", {
                            onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                                const { value } = event.target;
                                setValue(
                                    "registration_number",
                                    value
                                        .replaceAll(" ", "")
                                        .replaceAll(/[\W_]+/g, "")
                                        .toUpperCase()
                                );
                            },
                        })}
                        hasError={!!formState.errors.registration_number}
                        errorText={formState?.errors?.registration_number?.message as string}
                        value={watch("registration_number")}
                        label="Registration number*"
                    />
                    <TextInput
                        {...register("mileage", {
                            min: {
                                message: "Please enter a number above 0",
                                value: 0,
                            },
                            max: {
                                message: `Please enter a number below ${MAX_MILEAGE}`,
                                value: MAX_MILEAGE,
                            },
                            onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                                const asNumber = parseInt(event.target.value, 10);
                                if (isNumber(asNumber)) {
                                    if (asNumber > MAX_MILEAGE)
                                        setValue("mileage", `${MAX_MILEAGE}`);
                                    if (asNumber <= 0) setValue("mileage", "");
                                }
                                if (`${asNumber}`.length > 6)
                                    setValue(
                                        "mileage",
                                        `${asNumber}`.substring(0, `${asNumber}`.length - 1)
                                    );
                            },
                        })}
                        onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                            handleKeyDownEvent(event, DIGITS_REGEX);
                        }}
                        onPaste={(event: React.ClipboardEvent<HTMLInputElement>) => {
                            handlePasteEvent(event, DIGITS_REGEX);
                        }}
                        min={0}
                        max={MAX_MILEAGE}
                        value={watch("mileage")}
                        hasError={!!formState.errors.mileage}
                        errorText={formState?.errors?.mileage?.message as string}
                        label="Mileage*"
                        type="number"
                        pattern="\d*"
                        inputMode="numeric"
                    />
                    <Button theme="primary" type="submit" fullWidth ariaLabel="Find vehicle">
                        Find vehicle
                    </Button>
                </form>
            </div>
        </Wrapper>
    ) : null;
};

export default ValuePartExchangeForm;
